/**
 * The dock's two charts, plain SVG:
 * - SunPath: the sky as seen from the roof (stereographic-like polar plot: the horizon is the outer ring, the zenith
 *   the centre, north up), the sun's track on the solstices, the equinox and the chosen day, solar-hour lines, the
 *   horizon the DSM makes around the roof (the shade mask), and the sun of the scrubber;
 * - ProfileChart: the cross-section, height above ground along the line from A to B for the DSM surface and our
 *   roof model, with a crosshair readout (moved through refs: no React render per pointer move).
 */
import { useMemo, useRef } from "react";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import { dayPath, hourLine, solarClock, sunAtSolarTime, type SunPosition } from "./sun.ts";

const SIZE = 196;
const R = 84;
const C = SIZE / 2;

function polar(p: SunPosition): [number, number] {
  const r = (R * (90 - Math.max(0, p.elevation))) / 90;
  const a = (p.azimuth * Math.PI) / 180;
  return [C + r * Math.sin(a), C - r * Math.cos(a)];
}

const pathOf = (pts: SunPosition[]) => pts.map((p, i) => `${i ? "L" : "M"}${polar(p).map((v) => v.toFixed(1)).join(" ")}`).join("");

export function SunPath({ lat, day, minutes, horizon }: { lat: number; day: number; minutes: number; horizon: number[] | null }) {
  const t = useT();
  const intl = useIntlShape();
  // the June solstice, the equinox and the December solstice
  const tracks = useMemo(() => [172, 79, 355].map((day) => ({ day, d: pathOf(dayPath(lat, day)) })), [lat]);
  const hours = useMemo(
    () =>
      Array.from({ length: 17 }, (_, i) => i + 4)
        .map((h) => ({ h, pts: hourLine(lat, h) }))
        .filter((x) => x.pts.length > 3)
        .map((x) => ({ h: x.h, d: pathOf(x.pts), label: polar(x.pts.reduce((a, b) => (b.elevation > a.elevation ? b : a))) })),
    [lat],
  );
  const today = useMemo(() => pathOf(dayPath(lat, day)), [lat, day]);
  const mask = useMemo(() => {
    if (!horizon) return null;
    const step = 360 / horizon.length;
    const outer = horizon.map((_, i) => polar({ azimuth: i * step, elevation: 0 }));
    const inner = horizon.map((h, i) => polar({ azimuth: i * step, elevation: h }));
    const ring = (pts: [number, number][]) => pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join("") + "Z";
    return ring(outer) + ring(inner.reverse());
  }, [horizon]);
  const sun = sunAtSolarTime(lat, day, minutes);
  const [sx, sy] = polar(sun);
  return (
    <svg className="an-sunpath" viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} role="img" aria-label={t("editor.charts.sunPathLabel", { azimuth: intl.formatNumber(Math.round(sun.azimuth)), elevation: intl.formatNumber(Math.round(sun.elevation)) })}>
      <circle cx={C} cy={C} r={R} className="sky" />
      {[30, 60].map((e) => (
        <circle key={e} cx={C} cy={C} r={(R * (90 - e)) / 90} className="grid" />
      ))}
      {Array.from({ length: 12 }, (_, i) => i * 30).map((a) => {
        const [x, y] = polar({ azimuth: a, elevation: 0 });
        return <line key={a} x1={C} y1={C} x2={x} y2={y} className="grid" />;
      })}
      {mask && <path d={mask} className="mask" fillRule="evenodd" />}
      {hours.map((h) => (
        <g key={h.h}>
          <path d={h.d} className="hour" />
          <text x={h.label[0]} y={h.label[1] - 3} className="hour-label">
            {h.h}
          </text>
        </g>
      ))}
      {tracks.map((tr) => (
        <path key={tr.day} d={tr.d} className="track" />
      ))}
      <path d={today} className="today" />
      {([t("editor.charts.compass.n"), t("editor.charts.compass.e"), t("editor.charts.compass.s"), t("editor.charts.compass.w")]).map((n, i) => {
        const [x, y] = polar({ azimuth: i * 90, elevation: -8 });
        return (
          <text key={i} x={x} y={y + 4} className="compass">
            {n}
          </text>
        );
      })}
      <text x={C + 3} y={C - (R * 60) / 90 + 9} className="elev">60°</text>
      <text x={C + 3} y={C - (R * 30) / 90 + 9} className="elev">30°</text>
      {sun.elevation > 0 && <circle cx={sx} cy={sy} r={6.5} className="sun" />}
      <title>{t("editor.charts.sunAt", { time: intl.formatTime(solarClock(minutes), { hour: "numeric", minute: "2-digit", timeZone: "UTC" }) })}</title>
    </svg>
  );
}

export interface ProfileSeries {
  label: string;
  className: string;
  values: number[];
}

/** Height along the section: one y axis (metres above ground), distance on x. */
export function ProfileChart({ distances, series, onHoverDistance }: { distances: number[]; series: ProfileSeries[]; onHoverDistance?: (d: number | null) => void }) {
  const t = useT();
  const intl = useIntlShape();
  const metres = (v: number, digits: number) => intl.formatNumber(v, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const W = 300;
  const H = 132;
  const pad = { l: 30, r: 8, t: 8, b: 20 };
  const length = distances[distances.length - 1] ?? 1;
  const all = series.flatMap((s) => s.values.filter(Number.isFinite));
  const top = Math.max(2, Math.ceil((Math.max(0, ...all) + 0.5) / 2) * 2);
  const bottom = Math.min(0, Math.floor(Math.min(0, ...all)));
  const x = (d: number) => pad.l + ((W - pad.l - pad.r) * d) / Math.max(length, 1e-6);
  const y = (h: number) => pad.t + ((H - pad.t - pad.b) * (top - h)) / (top - bottom || 1);
  const paths = series.map((s) => {
    let d = "";
    let pen = false;
    s.values.forEach((v, i) => {
      if (!Number.isFinite(v)) {
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(distances[i]!).toFixed(1)} ${y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  });
  const yTicks = Array.from({ length: 5 }, (_, i) => bottom + ((top - bottom) * i) / 4);
  const xStep = length > 40 ? 10 : length > 16 ? 5 : length > 6 ? 2 : 1;
  const xTicks = Array.from({ length: Math.floor(length / xStep) + 1 }, (_, i) => i * xStep);
  const cross = useRef<SVGGElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const move = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const d = Math.min(length, Math.max(0, ((px - pad.l) / (W - pad.l - pad.r)) * length));
    let i = 0;
    while (i < distances.length - 1 && distances[i + 1]! < d) i++;
    const g = cross.current;
    const tipEl = tip.current;
    if (!g || !tipEl) return;
    g.setAttribute("transform", `translate(${x(distances[i]!).toFixed(1)} 0)`);
    g.style.display = "";
    tipEl.style.display = "";
    tipEl.textContent = [
      t("editor.charts.tipDistance", { distance: metres(distances[i]!, 1) }),
      ...series.map((s) => (Number.isFinite(s.values[i]!) ? t("editor.charts.tipSeries", { label: s.label, height: metres(s.values[i]!, 2) }) : t("editor.charts.tipSeriesNone", { label: s.label }))),
    ].join(" · ");
    onHoverDistance?.(distances[i]!);
  };
  const leave = () => {
    if (cross.current) cross.current.style.display = "none";
    if (tip.current) tip.current.style.display = "none";
    onHoverDistance?.(null);
  };
  return (
    <div className="an-profile">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" onPointerMove={move} onPointerLeave={leave} role="img" aria-label={t("editor.charts.profileLabel")}>
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} className="grid" />
            <text x={pad.l - 4} y={y(v) + 3} className="tick" textAnchor="end">
              {intl.formatNumber(v, { maximumFractionDigits: 1 })}
            </text>
          </g>
        ))}
        {xTicks.map((v) => (
          <text key={v} x={x(v)} y={H - 6} className="tick" textAnchor="middle">
            {intl.formatNumber(v)}
          </text>
        ))}
        <text x={W - pad.r} y={H - 6} className="tick" textAnchor="end">
          m
        </text>
        {paths.map((d, i) => (
          <path key={series[i]!.label} d={d} className={series[i]!.className} />
        ))}
        <g ref={cross} style={{ display: "none" }}>
          <line y1={pad.t} y2={H - pad.b} className="cross" />
        </g>
        <text x={pad.l + 2} y={y(0) - 3} className="tick">A</text>
        <text x={W - pad.r - 2} y={y(0) - 3} className="tick" textAnchor="end">B</text>
      </svg>
      <div ref={tip} className="an-profile-tip" style={{ display: "none" }} />
      <div className="an-profile-legend">
        {series.map((s) => (
          <span key={s.label}>
            <i className={s.className} /> {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
